// ── Welle B3: Mitarbeit im Stundeneintrag ────────────────────────────────────────
// Eingeklappter Teil „Mitarbeit (optional)“: je Kind die Werte 1–6, ein Tippen genügt.
// Fach aus dem Eintrag, Woche aus dem Datum. Ein Wert dieser Woche ist vorausgewählt; ein
// anderer ersetzt ihn (P1a „ersetzen“, Tendenz und Bemerkung aus dem Profil bleiben erhalten).
// Nicht angetippte Kinder bleiben unverändert, fehlende sind ausgegraut. Gespeichert wird mit
// „Speichern“ des Eintrags über POST /api/schueler/:id/mitarbeit — dieselbe Route wie im Profil.
import React, { useCallback, useEffect, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { apiGet, apiPost } from '../../lib/api';
import './MitarbeitImEintrag.css';

type Vorhanden = { wert: number; tendenz: '+' | '-' | null; bemerkung: string | null };
type Antwort = { woche: { von: string; bis: string }; fach: string; erlaubt: boolean; grund: string | null; werte: Record<string, Vorhanden> };

export type MitarbeitZustand = {
  offen: boolean; setOffen: (o: boolean) => void;
  daten: Antwort | null; fehler: string;
  gewaehlt: Record<number, number>; tippe: (id: number, wert: number) => void;
  aenderungen: (fehlend: Set<number>) => { id: number; wert: number; alt: Vorhanden | null }[];
  speichern: (fehlend: Set<number>) => Promise<Record<number, string>>;
};

/** Zustand für einen Eintrag (Klasse, Fach, Datum). Lädt erst beim Aufklappen. */
// Welle K2: startOffen = gleich aufgeklappt (Fenster „Stunde eintragen“); NachtragenSheet bleibt eingeklappt
export function useMitarbeitImEintrag(klasseId: number | null, fach: string, datum: string, startOffen = false): MitarbeitZustand {
  const [offen, setOffen] = useState(startOffen);
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Record<number, number>>({});
  const f = fach.trim();

  useEffect(() => { setDaten(null); setGewaehlt({}); setFehler(''); }, [klasseId, f, datum]);
  useEffect(() => {
    if (!offen || daten || !klasseId || !f) return;
    apiGet<Antwort>(`/api/klassenbuch/mitarbeit?klasse_id=${klasseId}&fach=${encodeURIComponent(f)}&datum=${datum}`)
      .then(setDaten).catch((e: any) => setFehler(e?.message ?? 'Mitarbeit konnte nicht geladen werden.'));
  }, [offen, daten, klasseId, f, datum]);

  const tippe = useCallback((id: number, wert: number) => setGewaehlt(g => {
    const n = { ...g };
    if (n[id] === wert) delete n[id]; else n[id] = wert;   // erneutes Tippen nimmt die Auswahl zurück
    return n;
  }), []);

  const aenderungen = useCallback((fehlend: Set<number>) => {
    if (!daten?.erlaubt) return [];
    return Object.entries(gewaehlt).map(([id, wert]) => ({ id: Number(id), wert, alt: daten.werte[id] ?? null }))
      .filter(a => !fehlend.has(a.id) && a.alt?.wert !== a.wert);
  }, [daten, gewaehlt]);

  const speichern = useCallback(async (fehlend: Set<number>) => {
    const fehl: Record<number, string> = {};
    for (const a of aenderungen(fehlend)) {
      try {
        await apiPost(`/api/schueler/${a.id}/mitarbeit`, {
          fach: f, woche: datum, wert: a.wert, ersetzen: true,
          // Tendenz und Bemerkung eines schon eingetragenen Werts bleiben erhalten
          tendenz: a.alt?.tendenz ?? null, bemerkung: a.alt?.bemerkung ?? null,
        });
      } catch (e: any) { fehl[a.id] = e?.message ?? 'Nicht gespeichert.'; }
    }
    if (!Object.keys(fehl).length) {
      // gespeichert → neu laden, damit „vorhanden“ stimmt
      setDaten(null); setGewaehlt({});
    }
    return fehl;
  }, [aenderungen, f, datum]);

  return { offen, setOffen, daten, fehler, gewaehlt, tippe, aenderungen, speichern };
}

/** ISO-Kalenderwoche eines Datums (nur Anzeige) */
export function kalenderwoche(iso: string): number {
  const d = new Date(iso.slice(0, 10) + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7) + 3);   // Donnerstag dieser Woche
  const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  jan4.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((d.getTime() - jan4.getTime()) / (7 * 86400000));
}

// Welle K2: Kopf „Mathe, KW 43“ mit „3 von 25 eingetragen“, Werte 1–6 neutral, gewählt in der Hauptfarbe,
// fehlende Kinder ausgegraut mit „fehlt heute“; zweispaltig am PC. Funktion wie seit B3.
export function MitarbeitImEintrag({ z, kinder, fehlend, fehlgeschlagen = {}, zweispaltig = false }: {
  z: MitarbeitZustand; kinder: { id: number; name: string }[]; fehlend: Set<number>; fehlgeschlagen?: Record<number, string>; zweispaltig?: boolean;
}) {
  const n = z.aenderungen(fehlend).length;
  const da = kinder.filter(k => !fehlend.has(k.id));
  const eingetragen = z.daten?.erlaubt ? da.filter(k => (z.gewaehlt[k.id] ?? z.daten!.werte[k.id]?.wert) != null).length : null;
  return (
    <section className={`ld-mie${z.offen ? ' ist-offen' : ''}`}>
      <button type="button" className="ld-mie__kopf" aria-expanded={z.offen} onClick={() => z.setOffen(!z.offen)}>
        <span>{z.daten ? `${z.daten.fach}, KW ${kalenderwoche(z.daten.woche.von)}` : 'Mitarbeit'}
          {eingetragen != null && <small>{eingetragen} von {da.length} eingetragen</small>}</span>
        {n > 0 && <b className="ld-mie__zahl" title="neu gewählt">{n}</b>}
        <MIcon name={z.offen ? 'expand_less' : 'expand_more'} size={20} />
      </button>
      {z.offen && (
        <div className="ld-mie__inhalt">
          {z.fehler ? <p className="ld-mie__hinweis" role="alert">{z.fehler}</p>
            : !z.daten ? <p className="ld-mie__hinweis">Lädt …</p>
            : !z.daten.erlaubt ? <p className="ld-mie__hinweis">{z.daten.grund}</p>
            : (
              <ul className={`ld-mie__liste${zweispaltig ? ' ld-mie__liste--zwei' : ''}`}>
                {kinder.map(k => {
                  const weg = fehlend.has(k.id);
                  const alt = z.daten!.werte[k.id]?.wert ?? null;
                  const wahl = z.gewaehlt[k.id] ?? alt;
                  return (
                    <li key={k.id} className={`ld-mie__kind${weg ? ' ist-fehlend' : ''}${fehlgeschlagen[k.id] ? ' ist-fehler' : ''}`}>
                      <span className="ld-mie__name">{k.name}{weg && <small>fehlt heute</small>}{fehlgeschlagen[k.id] && <small role="alert">{fehlgeschlagen[k.id]}</small>}</span>
                      <span className="ld-mie__werte" role="group" aria-label={`Mitarbeit ${k.name}`}>
                        {[1, 2, 3, 4, 5, 6].map(w => (
                          <button key={w} type="button" disabled={weg} aria-pressed={wahl === w}
                            className={`ld-mie__wert${wahl === w ? ' an' : ''}${alt === w && z.gewaehlt[k.id] == null ? ' ist-alt' : ''}`}
                            onClick={() => z.tippe(k.id, w)}>{w}</button>
                        ))}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
        </div>
      )}
    </section>
  );
}
